import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Customer from './Customer.tsx';
import { editUser } from '../../../services/admin/UserService.tsx';
import { Users } from '../../../models/User.tsx';
import '../../../styles/admin/Customer.css';
import { RiFileExcel2Line } from "react-icons/ri";
import * as XLSX from 'xlsx';
import { IoIosClose } from "react-icons/io";
import { IoSettings } from "react-icons/io5";
import { CgMenuGridR } from "react-icons/cg";
import { FaUserPlus } from "react-icons/fa6";
import { Link } from 'react-router-dom';
import { useUser } from '../../../context/UserContext.tsx';
import AgentContext from '../../../context/admin/Agent.tsx';
import { filterMessagingUsers } from '../../../helpers/messagingRecipients.ts';
import {
    formatAdminFiguresDateLabel,
    formatAdminFiguresTime,
} from '../../../helpers/adminFiguresTime.tsx';

const CustomerAdmin = () => {

    const { user: currentUser } = useUser();
    const { allPlayers, loading: agentLoading, refreshRoster, updatePlayerInContext, removePlayerFromContext } = useContext(AgentContext);
    const [customer, setCustomer] = useState<Users[]>([]);
    const [originalCustomer, setOriginalCustomer] = useState<Users[]>([]);
    const [filters, setFilters] = useState<Users['filters'] | undefined>(undefined);
    const [searchResults, setSearchResults] = useState<Users[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const seededFromRosterRef = useRef(false);

    useEffect(() => {
        refreshRoster();
    }, [refreshRoster]);

    if (!currentUser) {
        throw new Error("User not authenticated");
    }
    const updatePlayerInList = useCallback((playerId: string, updates: Partial<Users>) => {
        updatePlayerInContext(playerId, updates);

        const patch = (list: Users[]) =>
            list.map(p => (p.id === playerId ? { ...p, ...updates } : p));

        setCustomer(prev => patch(prev));
        setOriginalCustomer(prev => patch(prev));
    }, [updatePlayerInContext]);

    const removePlayerFromList = useCallback((playerId: string) => {
        removePlayerFromContext(playerId);

        const drop = (list: Users[]) => list.filter(p => p.id !== playerId);
        setCustomer(prev => drop(prev));
        setOriginalCustomer(prev => drop(prev));
    }, [removePlayerFromContext]);

    useEffect(() => {
        if (!currentUser) return;

        if (agentLoading && allPlayers.length === 0) {
            setIsLoading(true);
            return;
        }

        const mergePlayerUpdates = (list: Users[]) => {
            if (list.length === 0) {
                if (seededFromRosterRef.current) return [];
                if (allPlayers.length > 0) seededFromRosterRef.current = true;
                return allPlayers;
            }
            seededFromRosterRef.current = true;
            const playerMap = new Map(allPlayers.map((p: Users) => [p.id, p]));
            return list.flatMap(p => {
                if (!p.id) return [p];
                const updated = playerMap.get(p.id);
                return updated ? [{ ...p, ...updated }] : [];
            });
        };

        setCustomer(prev => mergePlayerUpdates(prev));
        setOriginalCustomer(prev => mergePlayerUpdates(prev));
        setFilters(currentUser.filters);
        setIsLoading(false);
    }, [currentUser, allPlayers, agentLoading]);

    useEffect(() => {
        if (customer.length > 0 && originalCustomer.length === 0) {
            setOriginalCustomer(customer);
        }
    }, [customer]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setSearchQuery(value);

        const roster = originalCustomer.length > 0 ? originalCustomer : allPlayers;

        if (value.trim() === '') {
            setSearchResults([]);
            setCustomer(roster);
            return;
        }

        const matches = filterMessagingUsers(roster, value.trim());
        setSearchResults(matches);
        setCustomer(matches);
    };
    const handleBlur = () => {
        setTimeout(() => {
            if (!isLoading) {
                setSearchResults([]);
            }
        }, 200);
    };

    const handleResultSelect = (userName: string) => {
        setSearchQuery(userName);

        const roster = originalCustomer.length > 0 ? originalCustomer : allPlayers;
        const selectedPlayer = roster.filter(player => player.userName === userName);
        setCustomer(selectedPlayer);

        setTimeout(() => {
            setSearchResults([]);
        }, 200);
    };

    const handleFilterToggle = (filterKey: string) => {
        setFilters((prevFilters) => {
            if (!prevFilters) return undefined;

            return {
                ...prevFilters,
                [filterKey]: !prevFilters[filterKey],
            };
        });
    };


    const handleApplyClick = async () => {
        try {
            if (!currentUser || !currentUser.id) {
                throw new Error("User not found or ID is missing");
            }
            if (!filters) {
                throw new Error("Filters are not defined");
            }
            await editUser(currentUser.id, { filters });
        } catch (error) {
            console.error('Failed to update filters:', error);
        }
    };

    const exportToExcel = () => {
        const input = document.querySelector('.table-responsive');
        if (input) {

            const tableClone = input.cloneNode(true) as HTMLElement;
            const ws = XLSX.utils.table_to_sheet(tableClone);

            ws['!cols'] = [
                { wpx: 125 }, // Customer
                { wpx: 125 }, // Password
                { wpx: 125 }, // Credit Limit
                { wpx: 125 },  // Wager Limet
                { wpx: 125 },  // Balance
                { wpx: 125 },  // P. Max Bet
                { wpx: 125 },  // T.Max Bet
                { wpx: 125 },  // Status
            ];

            const passwordColumnIndex = 1;
            const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
            for (let R = range.s.r + 1; R <= range.e.r; ++R) {
                const cell_address = XLSX.utils.encode_cell({ r: R, c: passwordColumnIndex });
                const cell = ws[cell_address];
                if (cell && typeof cell.v === 'number') {
                    cell.t = 's';
                    cell.v = cell.v.toString();
                }
            }

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, currentUser!.userName);

            const today = new Date();
            const formattedDate = formatAdminFiguresDateLabel(today, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
            const formattedTime = formatAdminFiguresTime(today, {
                hour: 'numeric',
                minute: '2-digit',
            });

            const cleanTime = formattedTime.replace(':', '_').replace(' ', '');
            const fileName = `CustomerAdmin_ ${formattedDate.replace(',', '')} ${cleanTime}.xlsx`;

            XLSX.writeFile(wb, fileName);

        } else {
            console.error("Item not found");
        }
    };


    return (
        <>

            <div className='container-fluid'>
                <div className='mt-2 container-body'>
                    <div className="row">
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="fixed-customer-admin">
                                <CgMenuGridR className={`icon-options`} />
                                <span>Customer Admin</span>
                            </div>

                            <div className="input-group input-group-sm flex-nowrap ms-auto inputs">
                                <span className="input-group-text">Player</span>
                                <input type="text" className="inputTransaction" placeholder="Search players..." autoComplete="off"
                                    id="usersPlayer"
                                    name="recipientUsers"
                                    value={searchQuery}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                />
                                {searchResults.length > 0 && (
                                    <ul className="list-group">
                                        {searchResults.map((user) => (
                                            <li className="list-group-item" key={user.id ?? user.userName} onClick={() => handleResultSelect(user.userName)}>
                                                {user.userName}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="d-flex ">
                                <div className="setting-icon ms-2 d-flex align-items-center" data-bs-toggle="modal" data-bs-target="#settingCustomer">
                                    <IoSettings />
                                </div>
                                <Link to="/add-player" className="setting-icon ms-2 d-flex align-items-center">
                                    <FaUserPlus />

                                </Link>
                                <div className="ms-2 excel-icon" onClick={exportToExcel}>
                                    <RiFileExcel2Line />
                                </div>
                            </div>
                        </div>


                    </div>
                    <span>Click on any column heading to change an entire column</span>

                    <div className='table-responsive'>

                        <Customer customer={customer} user={currentUser} filters={filters} onPlayerUpdate={updatePlayerInList} onPlayerDeleted={removePlayerFromList} isLoading={isLoading} />
                    </div>


                </div>

            </div>



            <div className="modal fade modal-sm" id="settingCustomer" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-title-acount-admin">
                            <span className="font-account">Report Setting</span>
                            <IoIosClose className="close-icon" size={42} data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="container-account">
                            <div className="container-setting">
                                <span className="font-size">Password</span>
                                <div className="form-switch">
                                    <input className="form-check-input" type="checkbox" id="checkPass" checked={filters?.password || false} onChange={() => handleFilterToggle('password')} />
                                </div>
                            </div>
                            <div className="container-setting">
                                <span className="font-size">Credit Limit</span>
                                <div className="form-switch">
                                    <input className="form-check-input" type="checkbox" id="checkCredit" checked={filters?.creditLimit || false} onChange={() => handleFilterToggle('creditLimit')} />
                                </div>
                            </div>
                            <div className="container-setting">
                                <span className="font-size">Wager Limit</span>
                                <div className="form-switch">
                                    <input className="form-check-input" type="checkbox" id="checkWager" checked={filters?.wagerLimit || false} onChange={() => handleFilterToggle('wagerLimit')} />
                                </div>
                            </div>
                            <div className="container-setting">
                                <span className="font-size">Balance</span>
                                <div className="form-switch">
                                    <input className="form-check-input" type="checkbox" id="checkBalance" checked={filters?.balanceForwardCarry || false} onChange={() => handleFilterToggle('balanceForwardCarry')} />
                                </div>
                            </div>
                            <div className="container-setting">
                                <span className="font-size">Pending</span>
                                <div className="form-switch">
                                    <input className="form-check-input" type="checkbox" id="checkPending" checked={filters?.pendingBalance || false} onChange={() => handleFilterToggle('pendingBalance')} />
                                </div>
                            </div>
                            <div className="container-setting">
                                <span className="font-size">Email</span>
                                <div className="form-switch">
                                    <input className="form-check-input" type="checkbox" id="checkEmail" checked={filters?.email || false} onChange={() => handleFilterToggle('email')} />
                                </div>
                            </div>
                            <div className="container-setting">
                                <span className="font-size">Last Login</span>
                                <div className="form-switch">
                                    <input className="form-check-input" type="checkbox" id="checkLogin" checked={filters?.lastLogin || false} onChange={() => handleFilterToggle('lastLogin')} />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-center">
                            <button type="button" className="btn btn-success" onClick={handleApplyClick} data-bs-dismiss="modal">Apply</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default CustomerAdmin;


{/* <div className="modal fade modal-xl" id="addPlayer" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-title-acount-admin">
                            <span className="font-account">Create a new player</span>
                            <IoIosClose className="close-icon" size={42} data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="container-account">
                            <RegisterPlayer />
                        </div>
                    </div>
                </div>
            </div> */}